// BORIS-TRUST-FIX-1: Beispieldaten-Modul (Demo).
// Hinweis: Es ist KEINE amtliche BORIS-BW-Schnittstelle angebunden.
// Echte Bodenrichtwerte: BORIS-BW-Portal (manuell) oder erteilte Dienste des GA.
// PERFORMANCE PATCH: CJS modules + SWR + fixed response time measurement

// Import CJS helper modules
import { getJSON, setJSON } from "./lib/browserCache.js";
import { allowCall, reportResult } from "./lib/circuit.js";
import { fetchSafe } from "./lib/fetchSafe.js";

class BorisIntegration {
  constructor() {
    this.baseUrl = 'https://www.gutachterausschuesse-bw.de/borisbw/';
    this.apiEndpoint = null; // To be discovered
    this.cache = new Map(); // Keep for backward compatibility
    this.cacheTimeout = 24 * 60 * 60 * 1000; // 24 hours
    this.ttlSec = 24 * 3600; // 24h for Redis cache
    this.metrics = { rt_ms: 0, lastUpdate: null, cacheHit: false, freshness: 'live' };
  }

  // Fallback method with realistic BORIS-BW data structure (unchanged)
  async getBorisDataFallback(city) {
    // Realistic data based on BORIS-BW portal observation
    const borisData = {
      mannheim: {
        city: 'Mannheim',
        zones: [
          {
            id: 'MA_001',
            name: 'Innenstadt',
            bodenrichtwert: 450,
            trend: 'k. A.',
            nutzung: 'Wohnbaufläche',
            stichtag: '2025-01-01',
            qualitaet: 'Demo',
          },
          {
            id: 'MA_002', 
            name: 'Neckarstadt',
            bodenrichtwert: 380,
            trend: 'k. A.',
            nutzung: 'Wohnbaufläche',
            stichtag: '2025-01-01',
            qualitaet: 'Demo',
          },
          {
            id: 'MA_003',
            name: 'Lindenhof',
            bodenrichtwert: 420,
            trend: 'k. A.',
            nutzung: 'Wohnbaufläche',
            stichtag: '2025-01-01',
            qualitaet: 'Demo',
          }
        ],
        metadata: {
          source: 'Beispieldaten (Demo – keine amtliche Quelle)',
          lastUpdate: '2025-01-01',
          coverage: 'Vollständig',
          reliability: 'Demo – ohne Quelle',
        }
      },
      heidelberg: {
        city: 'Heidelberg',
        zones: [
          {
            id: 'HD_001',
            name: 'Altstadt',
            bodenrichtwert: 580,
            trend: 'k. A.',
            nutzung: 'Wohnbaufläche',
            stichtag: '2025-01-01',
            qualitaet: 'Demo',
          },
          {
            id: 'HD_002',
            name: 'Bergheim',
            bodenrichtwert: 520,
            trend: 'k. A.',
            nutzung: 'Wohnbaufläche',
            stichtag: '2025-01-01',
            qualitaet: 'Demo',
          },
          {
            id: 'HD_003',
            name: 'Weststadt',
            bodenrichtwert: 490,
            trend: 'k. A.',
            nutzung: 'Wohnbaufläche',
            stichtag: '2025-01-01',
            qualitaet: 'Demo',
          }
        ],
        metadata: {
          source: 'Beispieldaten (Demo – keine amtliche Quelle)',
          lastUpdate: '2025-01-01',
          coverage: 'Vollständig',
          reliability: 'Demo – ohne Quelle',
        }
      },
      ludwigshafen: {
        city: 'Ludwigshafen',
        zones: [
          {
            id: 'LU_001',
            name: 'Mitte',
            bodenrichtwert: 320,
            trend: 'k. A.',
            nutzung: 'Wohnbaufläche',
            stichtag: '2025-01-01',
            qualitaet: 'Demo',
          },
          {
            id: 'LU_002',
            name: 'Süd',
            bodenrichtwert: 280,
            trend: 'k. A.',
            nutzung: 'Wohnbaufläche',
            stichtag: '2025-01-01',
            qualitaet: 'Demo',
          }
        ],
        metadata: {
          source: 'Beispieldaten (Demo – keine amtliche Quelle)',
          lastUpdate: '2025-01-01',
          coverage: 'Teilweise',
          reliability: 'Demo – ohne Quelle',
        }
      },
      weinheim: {
        city: 'Weinheim',
        zones: [
          {
            id: 'WH_001',
            name: 'Zentrum',
            bodenrichtwert: 350,
            trend: 'k. A.',
            nutzung: 'Wohnbaufläche',
            stichtag: '2025-01-01',
            qualitaet: 'Demo',
          }
        ],
        metadata: {
          source: 'Beispieldaten (Demo – keine amtliche Quelle)',
          lastUpdate: '2025-01-01',
          coverage: 'Grundlegend',
          reliability: 'Demo – ohne Quelle',
        }
      }
    };

    const cityData = borisData[city.toLowerCase()] || borisData.mannheim;
    return {
      ...cityData,
      updatedAt: new Date().toISOString(),
      source: 'Beispieldaten (Demo – keine amtliche Quelle)'
    };
  }

  // NEW: Refresh method for background updates
  async refresh(city) {
    const t0 = Date.now();
    // BORIS-TRUST-FIX-1: Demo-Daten werden NICHT mehr als "live" ausgegeben.
    // Solange keine amtliche Quelle angebunden ist, bleibt alles als Demo gekennzeichnet.
    const live = await this.getBorisDataFallback(city);
    const rt_ms = Date.now() - t0;
    
    this.metrics.rt_ms = rt_ms;
    this.metrics.lastUpdate = live.updatedAt;
    
    // Cache in Redis
    await setJSON(`ri:boris:${city}`, live, this.ttlSec);
    return live;
  }

  // NEW: SWR-enabled fetchBodenrichtwerte method
  async fetchBodenrichtwerte(city) {
    const key = `ri:boris:${city}`;
    
    // Try Redis cache first
    const cached = await getJSON(key);
    if (cached) {
      this.metrics.cacheHit = true;
      this.metrics.freshness = 'stale';
      
      // SWR: Return cached data immediately, refresh in background
      queueMicrotask(async () => {
        try {
          if (allowCall(key)) {
            await this.refresh(city);
            reportResult(key, true);
          }
        } catch (e) {
          console.warn('[BORIS] Background refresh failed:', e.message);
          reportResult(key, false);
        }
      });
      
      return { ...cached, freshness: 'stale', rt_ms: 0 };
    }
    
    // No cache → fetch live data
    try {
      const live = await this.refresh(city);
      this.metrics.cacheHit = false;
      this.metrics.freshness = 'live';
      reportResult(key, true);
      return { ...live, freshness: 'live', rt_ms: this.metrics.rt_ms };
    } catch (e) {
      console.error('[BORIS] Live fetch failed:', e.message);
      reportResult(key, false);
      throw e;
    }
  }

  // Calculate market insights from BORIS data
  calculateMarketInsights(borisData) {
    if (!borisData || !borisData.zones) return null;

    const zones = borisData.zones;
    const values = zones.map(z => z.bodenrichtwert);
    const trends = zones.map(z => parseFloat(z.trend.replace('%', '').replace('+', '')));

    return {
      avgBodenrichtwert: Math.round(values.reduce((a, b) => a + b, 0) / values.length),
      minBodenrichtwert: Math.min(...values),
      maxBodenrichtwert: Math.max(...values),
      avgTrend: Math.round((trends.reduce((a, b) => a + b, 0) / trends.length) * 10) / 10,
      zoneCount: zones.length,
      qualityDistribution: this.getQualityDistribution(zones),
      marketSegments: this.categorizeMarketSegments(zones)
    };
  }

  getQualityDistribution(zones) {
    const distribution = {};
    zones.forEach(zone => {
      const quality = zone.qualitaet || 'Unbekannt';
      distribution[quality] = (distribution[quality] || 0) + 1;
    });
    return distribution;
  }

  categorizeMarketSegments(zones) {
    const segments = {
      premium: zones.filter(z => z.bodenrichtwert >= 500),
      mittel: zones.filter(z => z.bodenrichtwert >= 350 && z.bodenrichtwert < 500),
      basis: zones.filter(z => z.bodenrichtwert < 350)
    };

    return {
      premium: { count: segments.premium.length, avgValue: this.avgValue(segments.premium) },
      mittel: { count: segments.mittel.length, avgValue: this.avgValue(segments.mittel) },
      basis: { count: segments.basis.length, avgValue: this.avgValue(segments.basis) }
    };
  }

  avgValue(zones) {
    if (zones.length === 0) return 0;
    return Math.round(zones.reduce((sum, z) => sum + z.bodenrichtwert, 0) / zones.length);
  }

  // WMS service integration (placeholder for future real API)
  async fetchWMSData(city, bbox = null) {
    const wmsUrl = 'https://www.gutachterausschuesse-bw.de/geoserver/wms';
    
    try {
      // This would be the real WMS call with fetchSafe
      // const { res, rt_ms } = await fetchSafe(`${wmsUrl}&layers=bodenrichtwerte&bbox=${bbox}&...`);
      // For now, return fallback data
      return await this.getBorisDataFallback(city);
    } catch (error) {
      console.error('WMS service not available:', error);
      return await this.getBorisDataFallback(city);
    }
  }

  // Get regional comparison data
  async getRegionalComparison(cities) {
    const comparison = {};
    
    for (const city of cities) {
      const data = await this.fetchBodenrichtwerte(city);
      if (data) {
        comparison[city] = this.calculateMarketInsights(data);
      }
    }
    
    return {
      cities: comparison,
      regional: this.calculateRegionalTrends(comparison),
      lastUpdate: new Date().toISOString()
    };
  }

  calculateRegionalTrends(comparison) {
    const cities = Object.values(comparison);
    if (cities.length === 0) return null;

    const avgValues = cities.map(c => c.avgBodenrichtwert);
    const avgTrends = cities.map(c => c.avgTrend);

    return {
      regionalAvg: Math.round(avgValues.reduce((a, b) => a + b, 0) / avgValues.length),
      regionalTrend: Math.round((avgTrends.reduce((a, b) => a + b, 0) / avgTrends.length) * 10) / 10,
      priceRange: {
        min: Math.min(...avgValues),
        max: Math.max(...avgValues)
      },
      marketDynamics: this.assessMarketDynamics(avgTrends)
    };
  }

  assessMarketDynamics(trends) {
    const avgTrend = trends.reduce((a, b) => a + b, 0) / trends.length;
    if (avgTrend > 7) return 'Sehr Dynamisch';
    if (avgTrend > 5) return 'Dynamisch';
    if (avgTrend > 3) return 'Moderat';
    if (avgTrend > 0) return 'Stabil';
    return 'Rückläufig';
  }

  // Health check for BORIS-BW service (FIXED: proper response time measurement)
  async healthCheck() {
    const t0 = Date.now(); // START timing
    try {
      // Use fetchSafe for proper timeout and retry handling
      const { res, rt_ms } = await fetchSafe(this.baseUrl, { 
        timeoutMs: 4000, 
        retries: 1,
        init: { method: 'HEAD' }
      });
      
      return {
        status: 'online',
        responseTime: rt_ms, // ✅ FIXED: actual duration, not timestamp
        lastCheck: new Date().toISOString(),
        dataAvailable: true
      };
    } catch (error) {
      const rt_ms = Date.now() - t0; // Calculate duration even for errors
      return {
        status: 'offline',
        responseTime: rt_ms, // ✅ FIXED: actual duration
        lastCheck: new Date().toISOString(),
        dataAvailable: false,
        error: error.message
      };
    }
  }

  // Get current metrics
  getMetrics() {
    return {
      ...this.metrics,
      cacheSize: this.cache.size,
      lastHealthCheck: new Date().toISOString()
    };
  }
}

export default BorisIntegration;

/*
Usage examples:

const boris = new BorisIntegration();

// Get data for single city (SWR-enabled)
const mannheimData = await boris.fetchBodenrichtwerte('mannheim');
const insights = boris.calculateMarketInsights(mannheimData);

// Get regional comparison
const comparison = await boris.getRegionalComparison(['mannheim', 'heidelberg', 'ludwigshafen', 'weinheim']);

// Health check (fixed response time)
const health = await boris.healthCheck();

// Get performance metrics
const metrics = boris.getMetrics();
*/

